import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import fetchAPI from 'shared/fetchAPI'

const DEFAULT_EVALUATE_URL = '/api/v3/feature-flags/:flag/evaluate'

const BackendFeatureFlagsContext = createContext({
  isReady: true,
  getFeatureValue: () => false,
  loadFeatureValue: () => {},
})

const normalizeContext = context => context || {}

const stableSerialize = value => {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`
  }

  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map(key => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(',')}}`
  }

  return JSON.stringify(value)
}

const cacheKey = (flagName, context) =>
  `${flagName}:${stableSerialize(normalizeContext(context))}`

const evaluateUrlFor = (evaluateUrl, flagName) =>
  (evaluateUrl || DEFAULT_EVALUATE_URL).replace(
    ':flag',
    encodeURIComponent(flagName)
  )

export const BackendProvider = ({ evaluateUrl, children }) => {
  const [values, setValues] = useState({})
  const inFlight = useRef({})

  const loadFeatureValue = useCallback(
    (flagName, context = {}) => {
      const key = cacheKey(flagName, context)
      if (
        Object.prototype.hasOwnProperty.call(values, key) ||
        inFlight.current[key]
      ) {
        return
      }

      inFlight.current[key] = true

      fetchAPI(evaluateUrlFor(evaluateUrl, flagName), {
        method: 'POST',
        body: { context: normalizeContext(context) },
      })
        .then(({ enabled }) => {
          setValues(currentValues => ({
            ...currentValues,
            [key]: Boolean(enabled),
          }))
        })
        .catch(error => {
          console.error('Failed to evaluate backend feature flag:', error)
          setValues(currentValues => ({
            ...currentValues,
            [key]: false,
          }))
        })
        .finally(() => {
          delete inFlight.current[key]
        })
    },
    [evaluateUrl, values]
  )

  const getFeatureValue = useCallback(
    (flagName, context = {}, defaultValue = false) => {
      const key = cacheKey(flagName, context)

      return Object.prototype.hasOwnProperty.call(values, key)
        ? values[key]
        : defaultValue
    },
    [values]
  )

  const value = useMemo(
    () => ({
      isReady: true,
      getFeatureValue,
      loadFeatureValue,
    }),
    [getFeatureValue, loadFeatureValue]
  )

  return (
    <BackendFeatureFlagsContext.Provider value={value}>
      {children}
    </BackendFeatureFlagsContext.Provider>
  )
}

export const useBackendStatus = () =>
  useContext(BackendFeatureFlagsContext).isReady

export const useBackendFeatureValue = () =>
  useContext(BackendFeatureFlagsContext).getFeatureValue

export const useBackendFeatureLoader = () =>
  useContext(BackendFeatureFlagsContext).loadFeatureValue
